import "./styles.scss";
import "moment/locale/pt";
import "moment/locale/en-gb";
import "moment/locale/es";

import { ConfigProvider, DatePicker, Tooltip } from "antd";

import { DateTime } from "luxon";
import { Icons } from "components";
import React from "react";
import { UserPlaceholder } from "utils/utils";
// @ts-ignore
import Variables from "styles/variables.scss";
import { connect } from "react-redux";
import en from "antd/es/locale/en_GB";
import es from "antd/es/locale/es_ES";
import moment from "moment";
import pt from "antd/es/locale/pt_PT";
import strings from "utils/strings";

const WEEK_DAYS = 5;

/**
 * Calendar/Schedule Component
 * @prop {array} schedule - Array of scheduled appointments
 * @prop {array} availability - (optional, default []) Availability returned by the calendar API as
 *   [{ date, slots: [{ start, end }] }]. `date` is the UTC midnight of the day (ISO string) and
 *   `start`/`end` are milliseconds since midnight in Europe/Lisbon wall-clock time (09:00 -> 32400000).
 *   Painted as green blocks under the appointments; it does not change the slot click behaviour.
 * @prop {string} date - Selected date in format "dd/LL/yyyy"
 * @prop {function} onDateChange - Callback when date changes
 * @prop {function} onClick - Callback when an appointment is clicked
 * @prop {function} onVideoClick - Callback when video call button is clicked
 * @prop {object} presence - { [consultationId]: { connected } }: the patient is in the Twilio room right now
 * @prop {function} onReportClick - Callback when report button is clicked
 * @prop {function} onSlotSelect - Callback when an empty slot is clicked
 * @prop {boolean} readOnly - Whether the calendar is read-only
 * @prop {boolean} mobile - Whether the calendar is in mobile view
 * @prop {boolean} hideHourLine - Whether to hide the current hour line
 * @prop {string} language - Current language
 */
class Schedule extends React.Component<any, any> {
	calendar: any;
	timer: any;

	constructor(props: any) {
		super(props);

		const dayOfWeek = DateTime.fromFormat(this.props.date, "dd/LL/yyyy").weekday;
		let formattedDates: Array<DateTime> = [];

		for (let x = 1; x <= 7; x++) {
			if (x < dayOfWeek) {
				formattedDates.push(DateTime.fromFormat(this.props.date, "dd/LL/yyyy").minus({ days: dayOfWeek - x }));
			} else if (x > dayOfWeek) {
				formattedDates.push(DateTime.fromFormat(this.props.date, "dd/LL/yyyy").plus({ days: x - dayOfWeek }));
			} else {
				formattedDates.push(DateTime.fromFormat(this.props.date, "dd/LL/yyyy"));
			}
		}

		this.state = {
			hours: [
				"07:00",
				"08:00",
				"09:00",
				"10:00",
				"11:00",
				"12:00",
				"13:00",
				"14:00",
				"15:00",
				"16:00",
				"17:00",
				"18:00",
				"19:00",
				"20:00",
				"21:00",
				"22:00",
				"23:00"
			],
			formattedDates,
			availabilityIntervals: this.getAvailabilityIntervals(),
			formatColors: [],
			lastMinute: DateTime.utc().minute,
			selectedDate: this.props.date
		};

		this.calendar = React.createRef();
	}

	componentDidMount() {
		if (this.calendar) {
			// const now = DateTime.utc();
			// const top =
			// 	-40 + now.hour * parseInt(Variables.cellHeight) + (now.minute / 60) * parseInt(Variables.cellHeight);

			// this.calendar.current.scroll({ top: top < 0 ? 0 : top, behavior: "smooth" });

			if (!this.props.hideHourLine) {
				this.timer = setInterval(() => this.checkTime(), 1000);
			}
		}
	}

	componentWillUnmount() {
		clearInterval(this.timer);
	}

	componentDidUpdate(prevProps: any) {
		if (this.props.date !== prevProps.date) {
			const dayOfWeek = DateTime.fromFormat(this.props.date, "dd/LL/yyyy").weekday;
			let formattedDates: Array<DateTime> = [];

			for (let x = 1; x <= 7; x++) {
				if (x < dayOfWeek) {
					formattedDates.push(
						DateTime.fromFormat(this.props.date, "dd/LL/yyyy").minus({ days: dayOfWeek - x })
					);
				} else if (x > dayOfWeek) {
					formattedDates.push(
						DateTime.fromFormat(this.props.date, "dd/LL/yyyy").plus({ days: x - dayOfWeek })
					);
				} else {
					formattedDates.push(DateTime.fromFormat(this.props.date, "dd/LL/yyyy"));
				}
			}

			this.setState({ formattedDates });
		}

		if (this.props.availability !== prevProps.availability) {
			this.setState({ availabilityIntervals: this.getAvailabilityIntervals() });
		}

		if (this.props.language !== prevProps.language) {
			this.setState({
				hours: [
					"07:00",
					"08:00",
					"09:00",
					"10:00",
					"11:00",
					"12:00",
					"13:00",
					"14:00",
					"15:00",
					"16:00",
					"17:00",
					"18:00",
					"19:00",
					"20:00",
					"21:00",
					"22:00",
					"23:00"
				]
			});
		}
	}

	checkTime() {
		const { lastMinute } = this.state;
		const now = DateTime.utc();

		let changed = false;
		if (now.minute !== lastMinute) {
			changed = true;
		}

		if (changed) {
			this.setState({ lastMinute: now.minute });
		}
	}

	getColor(status: string) {
		switch (status) {
			case "call":
				return "call";
			case "doneNoReport":
				return "done-no-report";
			case "future":
				return "future";
			case "done":
				return "done";
			default:
				return "future";
		}
	}

	getHourTopClass(now: any, hours: string[]) {
		const hourIndex = hours.findIndex((hour: string) => +hour.split(":")[0] === now.hour);
		const safeHourIndex = Math.max(0, hourIndex);

		return `schedule-time-now-hour-top--h-${safeHourIndex}-m-${now.minute}`;
	}

	getLineTopClass(now: any, hours: string[]) {
		const hourIndex = hours.findIndex((hour: string) => +hour.split(":")[0] === now.hour);
		const safeHourIndex = Math.max(0, hourIndex);

		return `schedule-time-now-line-top--h-${safeHourIndex}-m-${now.minute}`;
	}

	getCellTopClass(startDate: string) {
		const minute = DateTime.fromISO(startDate).minute;
		return `schedule-filled-cell-top--m-${minute}`;
	}

	getCellHeightClass(duration: number) {
		const safeDuration = Math.max(1, Math.min(360, Math.round(duration || 60)));
		return `schedule-filled-cell-height--d-${safeDuration}`;
	}

	/**
	 * Converts props.availability ([{ date, slots: [{ start, end }] }]) into luxon intervals in the browser's local zone.
	 * `date` is the UTC midnight of the day; `start`/`end` are wall-clock offsets (ms since midnight) in Europe/Lisbon.
	 * Slots with end <= start (or malformed entries) are ignored.
	 */
	getAvailabilityIntervals() {
		const { availability = [] } = this.props;
		const intervals: Array<{ start: DateTime; end: DateTime }> = [];

		if (!Array.isArray(availability)) return intervals;

		availability.forEach((schedule: any) => {
			if (!schedule || typeof schedule.date !== "string" || !Array.isArray(schedule.slots)) return;

			const day = DateTime.fromISO(schedule.date, { zone: "utc" });
			if (!day.isValid) return;

			// luxon 1.x: the zone is part of the object passed to fromObject
			const lisbonMidnight = DateTime.fromObject({
				year: day.year,
				month: day.month,
				day: day.day,
				zone: "Europe/Lisbon"
			});

			schedule.slots.forEach((slot: any) => {
				const start = Number(slot?.start);
				const end = Number(slot?.end);

				if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;

				intervals.push({
					start: lisbonMidnight.plus({ milliseconds: start }).toLocal(),
					end: lisbonMidnight.plus({ milliseconds: end }).toLocal()
				});
			});
		});

		return intervals;
	}

	/**
	 * Intersects the availability intervals with the cell [hour, hour + 60min) of the given day.
	 * Returns one block per non-empty intersection, as the start minute inside the cell (0-59)
	 * and the duration in minutes (1-60), ready to reuse the existing top/height helper classes.
	 */
	getCellAvailabilityBlocks(date: any, hour: number) {
		const { availabilityIntervals = [] } = this.state;
		const cellStart = date.set({ hour, minute: 0, second: 0, millisecond: 0 });
		const cellEnd = cellStart.plus({ minutes: 60 });
		const blocks: Array<{ minute: number; duration: number }> = [];

		availabilityIntervals.forEach((interval: any) => {
			const start = interval.start > cellStart ? interval.start : cellStart;
			const end = interval.end < cellEnd ? interval.end : cellEnd;

			if (end <= start) return;

			const minute = Math.max(0, Math.min(59, Math.floor(start.diff(cellStart, "minutes").minutes)));
			const duration = Math.max(1, Math.min(60 - minute, Math.round(end.diff(start, "minutes").minutes)));

			blocks.push({ minute, duration });
		});

		return blocks;
	}

	getDayIndicatorClass(weekday: number) {
		const safeDay = Math.max(0, Math.min(WEEK_DAYS - 1, weekday));
		return `schedule-day-indicator schedule-day-indicator--d-${safeDay}`;
	}

	renderDateHeaders() {
		const { selectedDate } = this.state;
		const { date, language, onDateChange, mobile } = this.props;
		let locale = pt;

		if (language === "en") {
			locale = en;
		} else if (language === "es") {
			locale = es;
		}

		return (
			<div className="schedule-date-handlers">
				<ConfigProvider locale={locale}>
					<DatePicker
						id="drawer_day"
						suffixIcon={null}
						allowClear={false}
						picker="month"
						value={(date && moment.utc(date, "DD/MM/YYYY")) || null}
						format="MMMM YYYY"
						inputReadOnly
						monthCellRender={(currentDate: any) => {
							return currentDate.format("MMM");
						}}
						onChange={(value: any) => {
							this.setState({ selectedDate: value.format("DD/MM/YYYY") });
							onDateChange(value.format("DD/MM/YYYY"), true);
						}}
					/>
				</ConfigProvider>
				<div className="schedule-day-selection">
					<button
						onClick={() => {
							if (mobile) {
								const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy").weekday;
								const newDate = DateTime.fromFormat(selectedDate, "dd/MM/yyyy")
									.minus({ days: date === 1 ? 7 - WEEK_DAYS + 1 : 1 })
									.toFormat("dd/MM/yyyy");

								this.setState({ selectedDate: newDate });
								onDateChange(newDate, date === 1 ? true : false);
							} else {
								const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy")
									.minus({ week: 1 })
									.toFormat("dd/MM/yyyy");
								const diff = DateTime.fromFormat(date, "dd/MM/yyyy").weekday - 1;
								const newDate = DateTime.fromFormat(date, "dd/MM/yyyy")
									.minus({ days: diff })
									.toFormat("dd/MM/yyyy");

								this.setState({ selectedDate: newDate });
								onDateChange(newDate, true);
							}
						}}
						className="btn --btn-flipped"
					>
						<Icons.Arrow color={Variables.primaryColor} />
					</button>
					<p
						onClick={() => {
							const now = DateTime.utc().toFormat("dd/MM/yyyy");

							if (selectedDate !== now) {
								this.setState({ selectedDate: now });
								onDateChange(now, true);
							}
						}}
					>
						{strings.ranges.today}
					</p>
					<button
						onClick={() => {
							if (mobile) {
								const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy").weekday;
								const newDate = DateTime.fromFormat(selectedDate, "dd/MM/yyyy")
									.plus({ days: date === WEEK_DAYS ? 7 - WEEK_DAYS + 1 : 1 })
									.toFormat("dd/MM/yyyy");

								this.setState({ selectedDate: newDate });
								onDateChange(newDate, date === WEEK_DAYS ? true : false);
							} else {
								const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy")
									.plus({ week: 1 })
									.toFormat("dd/MM/yyyy");
								const diff = DateTime.fromFormat(date, "dd/MM/yyyy").weekday - 1;
								const newDate = DateTime.fromFormat(date, "dd/MM/yyyy")
									.minus({ days: diff })
									.toFormat("dd/MM/yyyy");

								this.setState({ selectedDate: newDate });
								onDateChange(newDate, true);
							}
						}}
					>
						<Icons.Arrow color={Variables.primaryColor} />
					</button>
				</div>
			</div>
		);
	}

	renderLegend() {
		const { availability = [] } = this.props;

		if (!Array.isArray(availability) || availability.length === 0) return null;

		return (
			<div className="schedule-legend">
				<span className="schedule-legend-color" aria-hidden="true" />
				<p>{strings.appointments.availabilityLegend}</p>
			</div>
		);
	}

	renderCalendarHeaders() {
		const { formattedDates, selectedDate } = this.state;
		const { language, mobile, onDateChange } = this.props;

		return (
			<div className="schedule-headers">
				<div className="schedule-header --column-hours" />
				{formattedDates.map((actualDate: any, day: number) => {
					if (day >= WEEK_DAYS) return null;
					const defaultDate = formattedDates[day];
					const dayOfWeek: string = defaultDate.toFormat("d");
					const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy");
					const selected =
						date.day === actualDate.day && date.month === actualDate.month && date.year === actualDate.year;
					const now = DateTime.utc();
					const today =
						now.day === actualDate.day && now.month === actualDate.month && now.year === actualDate.year;

					return (
						<div key={`column_${day}`} className="schedule-header">
							<p
								onClick={() => {
									if (mobile) {
										this.setState({ selectedDate: actualDate.toFormat("dd/MM/yyyy") });
										onDateChange(actualDate.toFormat("dd/MM/yyyy"), false);
									}
								}}
							>
								<span>{actualDate.setLocale(language).weekdayShort?.substring(0, 3)}</span>
								{mobile ? (
									<span
										className={`schedule-date${today ? " --today" : ""}${
											selected ? " --date-selected" : ""
										}`}
									>
										{dayOfWeek}
									</span>
								) : (
									<span className={`schedule-date${today ? " --date-selected" : ""}`}>
										{dayOfWeek}
									</span>
								)}
							</p>
						</div>
					);
				})}
				<div className="schedule-header-fake-scroll" />
			</div>
		);
	}

	renderHours(now: any) {
		const { selectedDate, hours } = this.state;
		const { mobile, hideHourLine } = this.props;
		const lastHour = hours[hours.length - 1];
		const hourTopClass = this.getHourTopClass(now, hours);

		return (
			<div className="schedule-leading-column">
				{Array(hours.length)
					.fill("")
					.map((hourCell: any, index: number) => {
						const content = hours[index].split(":").slice(0, 2).join(":");
						const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy");

						let showHourLine = false;

						if (!hideHourLine) {
							if (mobile) {
								showHourLine =
									index === 0 &&
									now.year === date.year &&
									now.month === date.month &&
									now.day === date.day;
							} else {
								showHourLine =
									index === 0 && now.year === date.year && now.weekNumber === date.weekNumber;
							}

							if (now.hour > +lastHour.split(":")[0] || now.hour < +hours[0].split(":")[0]) {
								showHourLine = false;
							}
						}

						return (
							<React.Fragment key={`cell_${content}`}>
								{showHourLine && (
									<div className={`schedule-time-now --hour ${hourTopClass}`}>
										<p>{now.toFormat("HH:mm")}</p>
									</div>
								)}
								<div className="schedule-cell">
									<p>{content}</p>
								</div>
							</React.Fragment>
						);
					})}
				<div className="schedule-cell">
					<p>
						{lastHour === "23:00" ? "24:00" : `${String(+lastHour.split(":")[0] + 1).padStart(2, "0")}:00`}
					</p>
				</div>
			</div>
		);
	}

	renderCell(date: any, index: number, day: number) {
		const { hours } = this.state;
		const { schedule, onVideoClick, onClick, onReportClick, readOnly, onSlotSelect, presence } = this.props;

		const hasCell =
			schedule.filter(
				(tmpCell: any) =>
					DateTime.fromISO(tmpCell.startDate).toFormat("dd/MM/yyyy") === date.toFormat("dd/MM/yyyy") &&
					DateTime.fromISO(tmpCell.startDate).hour === +hours[index].split(":")[0]
			) || [];
		const now = DateTime.utc();
		const hour = hours[index].split(":");
		const disabled = hour[2]?.startsWith("disabled");
		const availabilityBlocks = this.getCellAvailabilityBlocks(date, +hour[0]);

		return (
			<div
				key={`cell_${index}`}
				data-disabled={disabled}
				className="schedule-cell"
				onClick={() => {
					// Only trigger slot selection if there's no existing cell and the slot isn't disabled
					if (hasCell.length === 0 && !disabled && typeof onSlotSelect === "function") {
						onSlotSelect({
							date: date.toFormat("dd/MM/yyyy"),
							hour: hours[index],
							dateTime: date.set({ hour: +hour[0], minute: 0 }).toISO()
						});
					}
				}}
			>
				{availabilityBlocks.map((block: any, blockIndex: number) => (
					<div
						key={`availability_${blockIndex}`}
						className={`schedule-availability schedule-filled-cell-top--m-${block.minute} schedule-filled-cell-height--d-${block.duration}`}
						aria-hidden="true"
					/>
				))}
				{hasCell.map((cell: any) => {
					const showButton =
						now >= DateTime.fromISO(cell?.startDate).minus({ minutes: 30 }) &&
						now <= DateTime.fromISO(cell?.endDate).plus({ minutes: 5 });

					let colorClass = "future";
					if (showButton) {
						colorClass = this.getColor("call");
					} else if (cell?.finished) {
						colorClass = this.getColor("done");
					} else if (!cell?.finished && DateTime.utc() > DateTime.fromISO(cell.endDate)) {
						colorClass = this.getColor("doneNoReport");
					} else {
						colorClass = this.getColor("future");
					}

					let showReport = false;
					if (!cell?.finished && DateTime.utc() > DateTime.fromISO(cell.startDate)) {
						showReport = true;
					}

					return (
						<div
							key={`cell_${cell._id}`}
							className={`schedule-filled-cell ${this.getCellTopClass(cell.startDate)} ${this.getCellHeightClass(
								cell.duration
							)} schedule-filled-cell--${colorClass}`}
							data-table-cell
							data-cell={cell._id}
							onClick={() => {
								if (typeof onClick === "function") {
									onClick(cell);
								}
							}}
						>
							<div className="schedule-filled-cell-background" />
							<div className="schedule-filled-cell-border" />
							<div className="schedule-filled-cell-content">
								{cell.user && (
									<div className="schedule-appointment-avatar">
										<img src={cell.user?.photo || UserPlaceholder} alt="appointment user avatar" />
									</div>
								)}
								<div className="schedule-client-detail">
									{cell.user ? (
										<>
											<p>{`${
												cell.consultationIndexNumber
													? `${strings.formatString(
															strings.generic.numeration as string,
															cell.consultationIndexNumber
													  )} | `
													: ""
											}${cell.user?.name}`}</p>
											<small>{cell.business?.name}</small>
											{presence?.[cell._id]?.connected && (
												<span className="schedule-presence">
													<span className="schedule-presence-dot" />
													{strings.formatString(strings.video.presenceInRoom, cell.user?.name || "")}
												</span>
											)}
										</>
									) : (
										<p className="schedule-client-title">{cell.title}</p>
									)}
								</div>
								{!readOnly && cell.user && (
									<React.Fragment>
										<div className="schedule-cell-options">
											{showReport && (
												<Tooltip title={strings.appointments.createReport} placement="topRight">
													<button
														onClick={(e: any) => {
															e.stopPropagation();

															if (typeof onReportClick === "function") {
																onReportClick(cell);
															}
														}}
														className="schedule-report-button"
													>
														<Icons.Report />
													</button>
												</Tooltip>
											)}
											{showButton && (
												<Tooltip title={strings.appointments.startCall} placement="topRight">
													<button
														onClick={(e: any) => {
															e.stopPropagation();

															if (typeof onVideoClick === "function") {
																onVideoClick(cell);
															}
														}}
														className="schedule-call-button"
													>
														<Icons.Camera />
													</button>
												</Tooltip>
											)}
										</div>
									</React.Fragment>
								)}
							</div>
						</div>
					);
				})}
				{disabled && hour[2].includes("=") && <p>{hour[2]?.replace("disabled=", "")}</p>}
			</div>
		);
	}

	renderCalendar(now: any) {
		const { formattedDates, selectedDate, hours } = this.state;
		const { mobile, hideHourLine } = this.props;

		const weekday = now.weekday - 1;
		const lineTopClass = this.getLineTopClass(now, hours);

		const date = DateTime.fromFormat(selectedDate, "dd/MM/yyyy");

		let showHourLine = false;
		if (!hideHourLine) {
			if (mobile) {
				showHourLine = now.year === date.year && now.month === date.month && now.day === date.day;
			} else {
				showHourLine = now.year === date.year && now.weekNumber === date.weekNumber;
			}

			if (now.hour > +hours[hours.length - 1].split(":")[0] || now.hour < +hours[0].split(":")[0]) {
				showHourLine = false;
			}
		}

		return (
			<div className="schedule-table-content">
				<div className={`schedule-time-now --line ${lineTopClass}`}>
					{showHourLine && (
						<div className="schedule-separator">
							{(!mobile && (
								<div className={this.getDayIndicatorClass(weekday)} />
							)) ||
								null}
						</div>
					)}
				</div>
				{formattedDates
					.filter((entry: any) => {
						if (mobile) {
							if (entry.toFormat("dd/MM/yyyy") === selectedDate) {
								return true;
							}

							return false;
						}

						return true;
					})
					.map((actualDate: any, day: number) => {
						if (day >= WEEK_DAYS) return null;

						return (
							<div key={`column_${day}`} className={`schedule-column${mobile ? " --mobile" : ""}`}>
								{Array(hours.length)
									.fill("")
									.map((hourCell: any, index: number) => this.renderCell(actualDate, index, day))}
								<div className="schedule-cell" />
							</div>
						);
					})}
			</div>
		);
	}

	render() {
		const { formattedDates } = this.state;
		const { readOnly } = this.props;

		if (!formattedDates.length) return null;
		const now = DateTime.utc().toLocal();

		return (
			<div data-days={WEEK_DAYS} className={`schedule-wrapper${readOnly ? " --read-only" : ""}`}>
				{this.renderDateHeaders()}
				{this.renderLegend()}
				{this.renderCalendarHeaders()}
				<div ref={this.calendar} className="schedule-main-container">
					<div className="schedule-table">
						{this.renderHours(now)}
						{this.renderCalendar(now)}
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	mobile: state.mobile
});

export default connect(mapStateToProps)(Schedule);
